import React, { memo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import { Content } from '../../theme/page';
import Typography from '../../atoms/typography';
import Heading from '../heading';
import Blocks from '../blocks';

// icons
import Head from '../../public/images/core-values/Head.svg';
import Marker from '../../public/images/core-values/Marker.svg';
import PairHearts from '../../public/images/core-values/Pair-Hearts.svg';
import Hands from '../../public/images/core-values/BIPOC.svg';
import Rocket from '../../public/images/core-values/Rocket.svg';
import Star from '../../public/images/core-values/Star.svg';

const Wrapper = styled.div`  
  padding-top: 4.4rem;
  padding-bottom: 1.2rem; 
  @media (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    padding: 6.4rem;
  }
`;

const IconStyling = css`
  margin-bottom: 1rem;
`;

const AllySection = styled.div`
  color: ${({ theme }) => theme.color.bluePrimary};
  display: flex;
  flex-direction: column;
  padding-top: 2.4rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    flex-direction: row;
  }
`;

const AllyImage = styled.div`
  margin-bottom: 1.5rem;
  img {
    max-width: 100%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    width: 40%;
    margin-right: 6%;
    margin-bottom: 0;
  }
`;

const AllyContent = styled.div`
  text-align: center;
  @media (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    width: 54%;
    text-align: left;
  }
`;

const AllyTitle = styled(Typography)`
  font-size: 3.2rem;
  margin-bottom: 1.6rem;
  padding-top: 0;

  @media (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    font-size: 4.8rem;
    margin-bottom: 3.2rem;
    line-height: 56px;
  }
`;

const AllyText = styled(Typography)`
  line-height: 1.5;
  margin-bottom: 3.2rem;

  @media (max-width: ${({ theme }) => theme.breakpoint.medium}) {
    font-size: 1.8rem;
  }
`;

const AllyLink = styled(Typography)`
  display: block;
  margin-bottom: 3.2rem;
  text-underline-position: under;
  text-underline-offset: 4px;
  text-decoration-thickness: 0.075em;

  @media (max-width: ${({ theme }) => theme.breakpoint.medium}) {
    font-size: 1.6rem;
  }
`;

const AllyLinkWithArrow = styled.span`
  background: url('/images/icon/Arrow-Next.svg') right 2px no-repeat;
  padding-right: 38px;

  @media (max-width: ${({ theme }) => theme.breakpoint.medium}) {
    background-size: 20px;
    padding-right: 28px;
  }
`;

const data = [
  {
    icon: <PairHearts css={IconStyling} />,
    title: 'Family matters',
    text: 'We believe in the power of families. Take care of yourself and the people you love.'
  },
  {
    icon: <Head css={IconStyling} />,
    title: 'Make magic',
    text: 'Be transformational, not just transactional. Bring joy, surprise-and-delight into every day.'
  },
  {
    icon: <Star css={IconStyling} />,
    title: 'Be brave',
    text: 'Try things and take risks. Learn from wins and losses and don’t be afraid to pivot.'
  },
  {
    icon: <Hands css={IconStyling} />,
    title: 'Respect and conviction',
    text: 'Healthy debate and honest conversations make us stronger. Engage respectfully and commit to a path forward.'
  },
  {
    icon: <Rocket css={IconStyling} />,
    title: 'Be the solution',
    text: 'Run towards problems and solve them. Be an owner; raise your hand when nobody else will.'
  },
  {
    icon: <Marker css={IconStyling} />,
    title: 'Make a mark',
    text: 'Be a force for good. Inspire the people you work with, the community you inhabit, and the industry at large.'
  }
];

const CoreValues = () => (
  <Content>
    <Wrapper>
      <Heading data={{ title: 'Core Values' }} careerUpdate />

      <Blocks data={data} coreValues />

      <AllySection>
        <AllyImage>
          <img src="/images/core-values/Ally-img.png" alt="Be an ally" />
        </AllyImage>

        <AllyContent>
          <AllyTitle element="h1" like="heading-2">
            Be an ally
          </AllyTitle>
          <AllyText element="p" like="dec-7">
            We value the beauty and power of diversity, equity, and inclusion. We
            are individually and collectively committed to identifying and
            dismantling harmful biases. We are empowered to hold ourselves and
            others accountable as we build and enact systems in pursuit of
            anti-racist, anti-discriminatory practices and culture.
          </AllyText>
        </AllyContent>
      </AllySection>
    </Wrapper>
  </Content>
);

export default memo(CoreValues);
